import { useState, useEffect } from 'react';
import { Button, Checkbox, Stack, Typography } from '@mui/material';
import { GenericDialog } from '../GenericDialog';
import { PageLoader } from '../PageLoader';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle, textButtonStyle } from '@/theme';

type MiRecalculationDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (evaluateManuallyAddedFields: boolean) => void;
  loading: boolean;
  entityType: 'assemblies' | 'POIs';
};

export const MiRecalculationDialog = ({
  open,
  onClose,
  onConfirm,
  loading,
  entityType,
}: MiRecalculationDialogProps) => {
  const [evaluateManuallyAddedFields, setEvaluateManuallyAddedFields] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Combine local processing state with prop loading state
  const isLoading = isProcessing || loading;

  const handleConfirm = () => {
    setIsProcessing(true);
    onConfirm(evaluateManuallyAddedFields);
  };

  // Reset processing state when dialog closes
  useEffect(() => {
    if (!open) {
      setIsProcessing(false);
    }
  }, [open]);

  const entityLabel = entityType === 'assemblies' ? 'assemblies' : 'POIs';

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      title="Re-run the MI Evaluation Process"
      content={
        <>
          <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
            {isLoading
              ? `Calculations are now being performed for all ${entityLabel} using the updated logic. This process may take some time.`
              : 'This action will trigger a full recalculation of the MI logic for this deployment. Are you sure you want to continue?'}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
            <Checkbox
              checked={evaluateManuallyAddedFields}
              onChange={(e) => setEvaluateManuallyAddedFields(e.target.checked)}
              sx={{ padding: 0 }}
              disabled={isLoading}
            />
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Evaluate Manually Added Fields
            </Typography>
          </Stack>
          {isLoading && (
            <Stack marginTop="3.2rem">
              <PageLoader variant="sm" flashing hideProgress />
            </Stack>
          )}
        </>
      }
      actions={
        <>
          {!isLoading && (
            <>
              <Button variant="text" onClick={onClose} sx={textButtonStyle}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleConfirm} sx={containedButtonStyle}>
                Confirm
              </Button>
            </>
          )}
        </>
      }
      actionsStyles={{ mr: 3, pb: 3 }}
    />
  );
};
